import { mapFormValuesToFieldNames, updateTrackingProperties } from './helper';
import * as v3 from './v3';
import * as v4 from './v4';

import type {
    TrackAnswerClickArgs,
    TrackFormSubmitArgs,
    TrackMultipleChoiceClickArgs,
} from '$lib/types/tracking';

// ── TrackPageArgs ────────────────────────────────────────────────────────

export interface TrackPageArgs {
    pageName: string;
    tag: string;
    campaignId: string;
    pageSlug: string;
    isLastPage: boolean;
    pageId: string;
    fromPageId: string | null;
    fromPageName: string | null;
}

// ── TrackingStrategy interface ───────────────────────────────────────────

export interface TrackingStrategy {
    trackButtonClick(buttonComponentId: string, buttonContent: { clickId: string }): void;
    trackImageClick(imageComponentId: string, clickId: string): void;
    trackFormSubmit(args: TrackFormSubmitArgs): void;
    trackMultipleChoiceClick(args: TrackMultipleChoiceClickArgs): void;
    trackAnswerClick(args: TrackAnswerClickArgs): void;
    trackCalendly(): void;
    trackCal(): void;
    trackGHL(): void;
    trackHubSpot(): void;
    trackIClosed(): void;
    trackPage(pageData: TrackPageArgs): void;
    trackVariant(pageName: string): void;
    trackCookies(integrations: string[]): void;
    trackBadgeImpression(campaignId: string, companyId: string): void;
    trackBadgeClick(campaignId: string, companyId: string): void;
}

// ── V3Strategy ───────────────────────────────────────────────────────────

export class V3Strategy implements TrackingStrategy {
    trackButtonClick(buttonComponentId: string, buttonContent: { clickId: string }): void {
        void buttonContent;
        v3.trackButtonClick(buttonComponentId);
    }

    trackImageClick(imageComponentId: string, clickId: string): void {
        void clickId;
        v3.trackImageClick(imageComponentId);
    }

    trackFormSubmit({ form, values, fields, button, inputMap }: TrackFormSubmitArgs): void {
        v3.trackFormSubmit({ formId: form._id, formValues: values, fields, inputMap });
        v3.trackButtonClick(button?._id);
    }

    trackMultipleChoiceClick({
        answers,
        question,
        fields,
        button,
    }: TrackMultipleChoiceClickArgs): void {
        const answerIds = answers.map(({ id }) => id);
        v3.trackMultipleChoiceClick({ answerIds, questionId: question.id, fields });
        v3.trackButtonClick(button?._id);
    }

    trackAnswerClick({ question, answer, fields }: TrackAnswerClickArgs): void {
        v3.trackAnswerClick({ answerId: answer?._id, questionId: question.id, fields });
        v3.trackButtonClick(answer?._id);
    }

    trackCalendly(): void {
        v3.trackCalendly();
    }

    trackCal(): void {
        v3.trackCal();
    }

    trackGHL(): void {
        v3.trackGHL();
    }

    trackHubSpot(): void {
        v3.trackHubSpot();
    }

    trackIClosed(): void {
        v3.trackIClosed();
    }

    trackPage(pageData: TrackPageArgs): void {
        v3.trackPage(
            {
                name: pageData.pageName,
                tag: pageData.tag,
                campaignId: pageData.campaignId,
                slug: pageData.pageSlug,
            },
            pageData.isLastPage,
        );
    }

    trackVariant(pageName: string): void {
        v3.trackVariant(pageName);
    }

    trackCookies(integrations: string[]): void {
        v3.trackCookies(integrations);
    }

    // V3 does not track the affiliate badge — intentional no-op
    trackBadgeImpression(): void {}

    trackBadgeClick(): void {}
}

// ── V4Strategy ───────────────────────────────────────────────────────────

export class V4Strategy implements TrackingStrategy {
    trackButtonClick(buttonComponentId: string, buttonContent: { clickId: string }): void {
        void buttonComponentId;
        v4.buttonClick(buttonContent.clickId);
    }

    trackImageClick(imageComponentId: string, clickId: string): void {
        void imageComponentId;
        v4.imageClick(clickId);
    }

    trackFormSubmit({
        form,
        values,
        fields,
        button,
        inputMap,
        suppressConversion,
    }: TrackFormSubmitArgs): void {
        const mappedValues = mapFormValuesToFieldNames(form, values, inputMap);
        updateTrackingProperties({ form, values, fields, inputMap });
        v4.formSubmit(mappedValues, button?.content.clickId, { suppressConversion });
    }

    trackMultipleChoiceClick({
        answers,
        question,
        button,
        campaignId,
    }: TrackMultipleChoiceClickArgs): void {
        v4.multipleChoiceSubmit(
            {
                text: question.content.plainText,
                fieldName: question.content.fieldName,
            },
            {
                text: answers
                    .map(({ plainText }) => plainText)
                    .sort()
                    .join('; '),
                clickId: button?.content?.clickId || '',
            },
            campaignId,
        );
    }

    trackAnswerClick({ question, answer }: TrackAnswerClickArgs): void {
        const questionDetails = {
            text: question.content.plainText,
            fieldName: question.content.fieldName,
        };

        // Tracked answer can be media or text answer
        const answerDetails = {
            text:
                answer?.componentType === 'questionMediaAnswer'
                    ? answer?.components[1].content.plainText
                    : answer?.content.plainText,
            clickId: answer?.content.clickId,
        };

        v4.answerClicked(questionDetails, answerDetails, answer?.campaignId);
    }

    trackCalendly(): void {
        v4.calendlyBooking();
    }

    trackCal(): void {
        v4.calBooking();
    }

    trackGHL(): void {
        v4.ghlBooking();
    }

    trackHubSpot(): void {
        v4.hubspotBooking();
    }

    trackIClosed(): void {
        v4.iclosedBooking();
    }

    trackPage(pageData: TrackPageArgs): void {
        v4.pageLoad(
            pageData.pageId,
            pageData.pageName,
            pageData.pageSlug,
            pageData.isLastPage,
            pageData.fromPageId,
            pageData.fromPageName,
        );
    }

    trackVariant(pageName: string): void {
        void pageName;
        // V4 does not track variants — intentional no-op
    }

    trackCookies(integrations: string[]): void {
        v4.cookiesAccept(integrations);
    }

    trackBadgeImpression(campaignId: string, companyId: string): void {
        v4.badgeImpression(campaignId, companyId);
    }

    trackBadgeClick(campaignId: string, companyId: string): void {
        v4.badgeClick(campaignId, companyId);
    }
}
